import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from '../../types';
import AICoachChat from './AICoachChat';
import AICoachCalendar from './AICoachCalendar';
import AICoachSettings from './AICoachSettings';
import DataImport from './DataImport';
import Analytics from './Analytics';
import TrainingGoals from './TrainingGoals';
import './AICoach.css';

interface AICoachProps {
  user: User;
}

type AICoachTab = 'chat' | 'calendar' | 'goals' | 'analytics' | 'data' | 'settings';

const AICoach: React.FC<AICoachProps> = ({ user }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AICoachTab>(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['chat', 'calendar', 'goals', 'analytics', 'data', 'settings'].includes(tab)) {
      return tab as AICoachTab;
    }
    return 'chat';
  });
  const [refreshCalendar, setRefreshCalendar] = useState(0);
  const [showWeeklyReview, setShowWeeklyReview] = useState(false);
  const [editGoalId, setEditGoalId] = useState<number | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Handle URL params (tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');

    if (tab && ['chat', 'calendar', 'goals', 'analytics', 'data', 'settings'].includes(tab)) {
      setActiveTab(tab as AICoachTab);
    }

    if (tab) {
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleSessionSelect = (sessionId: number) => {
    setActiveTab('chat');
  };

  const handleWorkoutCreated = () => {
    setRefreshCalendar(prev => prev + 1);
  };

  return (
    <div className="ai-coach-container">
      <p className="coach-page-subtitle">Load, fatigue, timing and next decisions</p>

      <div className="coach-tabs">
        {(['chat', 'calendar', 'goals', 'analytics', 'data', 'settings'] as AICoachTab[]).map(tab => (
          <button
            key={tab}
            className={`coach-tab ${activeTab === tab ? 'coach-tab-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'chat' ? t('aiCoach.tabs.chat') :
             tab === 'calendar' ? t('aiCoach.tabs.calendar') :
             tab === 'goals' ? t('aiCoach.tabs.goals') :
             tab === 'analytics' ? 'Analytics' :
             tab === 'data' ? 'Data' :
             t('aiCoach.tabs.settings')}
          </button>
        ))}
        {activeTab === 'chat' && (
          <button className="coach-weekly-reviews-btn" onClick={() => setShowWeeklyReview(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Weekly Reviews
          </button>
        )}
      </div>

      <div className="coach-content">
        {activeTab === 'chat' && (
          <AICoachChat user={user} onWorkoutCreated={handleWorkoutCreated} showWeeklyReview={showWeeklyReview} setShowWeeklyReview={setShowWeeklyReview} pendingMessage={pendingMessage} onPendingMessageConsumed={() => setPendingMessage(null)} />
        )}
        {activeTab === 'calendar' && (
          <AICoachCalendar user={user} refreshTrigger={refreshCalendar} onEditGoal={(goalId) => { setEditGoalId(goalId); setActiveTab('goals'); }} />
        )}
        {activeTab === 'goals' && (
          <TrainingGoals editGoalId={editGoalId} onEditGoalHandled={() => setEditGoalId(null)} onGeneratePlan={(goalTitle: string) => { setPendingMessage(`Zdefiniuj plan treningowy dla celu: ${goalTitle}`); setActiveTab('chat'); }} />
        )}
        {activeTab === 'analytics' && (
          <Analytics onSwitchToData={() => setActiveTab('data')} user={user} />
        )}
        {activeTab === 'data' && (
          <DataImport />
        )}
        {activeTab === 'settings' && (
          <AICoachSettings user={user} onSessionSelect={handleSessionSelect} />
        )}
      </div>
    </div>
  );
};

export default AICoach;
